import React, { useState } from "react";
import {
  FilterableCounts,
  FilterQuery,
  ParameterAndFilter,
  ParameterFilterOption,
  ParameterOrFilter,
  TextParameterFilterOption,
} from "../../../../../../microservices/functions/src/types/product_catalog/catalog_api_types";
import { partitionTopAndRest } from "../../helpers/partition_top_and_rest";
import { useToggleParameterFilter } from "../../helpers/use_toggle_parameter_filter";
import { makeAndFilter, makeOrFilter } from "../helpers/parameter_filter_utils";
import { ParameterTitleButton } from "./parameter_title_button";
import { ShowMoreButton } from "../show_more_button";
import { ParameterFilterChangeHandler } from "../types";

type ParameterSelectionChangeHandler = (
  parameterId: number,
  valueId: number,
  newSelectedState: boolean
) => void;

type TextParameterFilterItemProps = {
  value: string;
  count: number;
  parameterId: number;
  valueId: number;
  selected: boolean;
  onChange: ParameterSelectionChangeHandler;
};

const TextParameterFilterItem = ({
  value,
  count,
  parameterId,
  valueId,
  selected,
  onChange,
}: TextParameterFilterItemProps) => (
  <li>
    <label>
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onChange(parameterId, valueId, !selected)}
        disabled={count === 0 && !selected}
      />
      <span>
        {value} <b>({count})</b>
      </span>
    </label>
  </li>
);

const isSelected = (
  filteredParameters: FilterQuery["parameters"],
  parameterId: number,
  valueId: number
) => {
  const appliedFilter = filteredParameters?.[parameterId];

  if (
    appliedFilter === undefined ||
    (appliedFilter.type !== "and" && appliedFilter.type !== "or")
  ) {
    return false;
  } else {
    return appliedFilter.valueIds.includes(valueId);
  }
};

type TextParameterFilterControlsProps = {
  parameter: ParameterFilterOption;
  filterableCounts: FilterableCounts["textParameters"];
  allValues: TextParameterFilterOption[] | undefined;
  filteredParameters: FilterQuery["parameters"];
  onChange: ParameterFilterChangeHandler;
  onShowHint: (description: string) => void;
};

export const TextParameterFilterControls = ({
  parameter,
  filterableCounts,
  filteredParameters,
  onChange,
  allValues,
  onShowHint,
}: TextParameterFilterControlsProps): JSX.Element | null => {
  const countsByValueId: Record<number, number> =
    filterableCounts[parameter.id] ?? {};

  const [allShown, setAllShown] = useState(false);

  const [isExpanded, toggleIsExpanded] = useToggleParameterFilter(
    parameter,
    filteredParameters?.[parameter.id]
  );

  if (!allValues) {
    return null;
  }

  const [topOptions, restOptions] = partitionTopAndRest(allValues);

  const handleChange: ParameterSelectionChangeHandler = (
    parameterId,
    valueId,
    newSelectedState
  ) => {
    const currentFilterOnThisParameter =
      filteredParameters?.[parameterId]?.type !== "range"
        ? (filteredParameters?.[parameterId] as
            | ParameterAndFilter
            | ParameterOrFilter)
        : undefined;

    onChange(
      parameter.id,
      parameter.displayType === "multi"
        ? makeAndFilter(
            currentFilterOnThisParameter as ParameterAndFilter,
            valueId,
            newSelectedState
          )
        : makeOrFilter(
            currentFilterOnThisParameter as ParameterOrFilter,
            valueId,
            newSelectedState
          )
    );
  };

  return (
    <fieldset>
      <ParameterTitleButton
        parameter={parameter}
        isExpanded={isExpanded}
        toggleIsExpanded={toggleIsExpanded}
        onShowHint={onShowHint}
      />
      {isExpanded && (
        <ul>
          {topOptions.map((option) => (
            <TextParameterFilterItem
              key={option.i}
              value={option.v}
              count={countsByValueId[option.i] ?? 0}
              parameterId={parameter.id}
              valueId={option.i}
              onChange={handleChange}
              selected={isSelected(filteredParameters, parameter.id, option.i)}
            />
          ))}
          {allShown &&
            restOptions.map((option) => (
              <TextParameterFilterItem
                key={option.i}
                value={option.v}
                count={countsByValueId[option.i] ?? 0}
                parameterId={parameter.id}
                valueId={option.i}
                onChange={handleChange}
                selected={isSelected(
                  filteredParameters,
                  parameter.id,
                  option.i
                )}
              />
            ))}
          {restOptions.length > 0 &&
            (allShown ? (
              <ShowMoreButton isExpanded onClick={() => setAllShown(false)}>
                Skrýt
              </ShowMoreButton>
            ) : (
              <ShowMoreButton onClick={() => setAllShown(true)}>
                Zobrazit další…
              </ShowMoreButton>
            ))}
        </ul>
      )}
    </fieldset>
  );
};
